import type { Post } from '@/lib/firebase/posts';
import { auth } from '@/lib/firebase/config';

export interface EmailPostData {
    post: Post;
}

export interface EmailRecipientRecord {
    id: string;
    postId: string;
    creatorId: string;
    recipientId: string;
    recipientEmail: string;
    sentAt: Date;
    subject: string;
}

export interface EmailSendHistory {
    success: boolean;
    emailRecipients: EmailRecipientRecord[];
    recipientsByDate: Record<string, EmailRecipientRecord[]>;
    totalRecipients: number;
    totalSends: number;
}

export async function sendInvites(inviter: { name?: string; email?: string; userId?: string }, emails: string[]): Promise<void> {
    try {
        // ✅ Get Firebase Auth token
        const user = auth.currentUser;
        if (!user) {
            throw new Error('You must be signed in to send invitations');
        }
        const token = await user.getIdToken();

        const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
        const overrideUrl = import.meta.env.VITE_SEND_INVITES_URL as string | undefined;
        const url = overrideUrl || (base ? `${base}/sendInvites` : '/sendInvites');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`, // ✅ Include auth token
            },
            body: JSON.stringify({ inviterName: inviter.name, inviterEmail: inviter.email, inviterUserId: inviter.userId, emails })
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({} as any));
            throw new Error(data.error || `Failed to send invitations (HTTP ${response.status})`);
        }
    } catch (err: any) {
        console.error('Failed to send invites', err);
        throw new Error(err?.message || 'Failed to send invitations');
    }
}

export async function sendPostToFollowers(data: EmailPostData): Promise<void> {
    const { post } = data;

    try {
        // ✅ Get Firebase Auth token
        const user = auth.currentUser;
        if (!user) {
            throw new Error('You must be signed in to send emails');
        }
        const token = await user.getIdToken();

        const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
        const overrideUrl = import.meta.env.VITE_SEND_POST_URL as string | undefined;
        const url = overrideUrl || (base ? `${base}/sendPostToFollowers` : '/sendPostToFollowers');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`, // ✅ Include auth token
            },
            body: JSON.stringify({
                postId: post.id,
                creatorId: post.userId,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to send email');
        }

        const result = await response.json();
        console.log('Email sent successfully:', result);
    } catch (error: any) {
        console.error('Error sending email:', error);
        throw new Error(error.message || 'Failed to send email to subscribers');
    }
}

export async function getEmailSendHistory(postId: string): Promise<EmailSendHistory> {
    try {
        // ✅ Get Firebase Auth token
        const user = auth.currentUser;
        if (!user) {
            throw new Error('You must be signed in to view email history');
        }
        const token = await user.getIdToken();

        const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
        const overrideUrl = import.meta.env.VITE_GET_EMAIL_HISTORY_URL as string | undefined;
        const url = overrideUrl || (base ? `${base}/getEmailSendHistory` : '/getEmailSendHistory');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`, // ✅ Include auth token
            },
            body: JSON.stringify({
                postId: postId,
            }),
        });

        if (!response.ok) {
            // `await response.json()` on a 404 (empty body) throws
            // "Unexpected end of JSON input", which REPLACES the useful status
            // with a parse error -- that is why these endpoints 404ing in
            // production read as a JSON bug for however long it has been broken.
            const raw = await response.text().catch(() => '');
            let msg = '';
            try { msg = JSON.parse(raw)?.error ?? ''; } catch { /* not JSON */ }
            throw new Error(msg || `Failed to get email history (HTTP ${response.status})`);
        }

        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error getting email history:', error);
        throw new Error(error.message || 'Failed to get email send history');
    }
}
